import { serviceAgreement } from "@src/utilities/objects";
import type { AddressFields, ContactFields } from "@utils/BasicTypes";
import { validateSideTab } from "@utils/general";
import { sanitize, validateAddress, validateContact } from "@utils/validation";
import { debounceStorage } from "../../../../..";
import { ui } from "../../../../../interfaces/interfaces";

const FORM_VALIDATION_DELAY = 150;

export const customerMiddleware = async (
	action: { payload: { name: string; value: any } },
	next: any,
) => {
	switch (action.payload.name) {
		// all actions that are not in the switch statement are passed to the next middleware
		// in the chain, or ultimately to the reducer.
		// Actions below are handled by this middleware.
		// All cases directly below are related to validating the customer form on care plan stage 3.
		// TODO: Clarify  case "CarePlanCustomerAddressBusinessName": We do not offer currently Care Plan for businesses
		case "CarePlanCustomerAddressProperty":
		case "CarePlanCustomerAddressCity":
		case "CarePlanCustomerAddressLocality":
		case "CarePlanCustomerAddressPostcode":
		case "CarePlanCustomerAddressStreet":
		case "CarePlanCustomerAddressBusinessName":
		case "CarePlanCustomerContactEmail":
		case "CarePlanCustomerContactFirstName":
		case "CarePlanCustomerContactLandline":
		case "CarePlanCustomerContactLastName":
		case "CarePlanCustomerContactMobile":
		case "CarePlanCustomerContactTitle": {
			const stage = "CarePlan";
			const step = "Customer";
			const property = action.payload.name.replace(stage + step, "");
			const subStep = property.includes("Address") ? "Address" : "Contact";

			const Address = serviceAgreement.serviceAgreementCustomerAddress;
			const Customer = serviceAgreement.serviceAgreementCustomer;
			const sanitizeProperty = property.replace(subStep, "");

			// sanitize the input
			action.payload.value = sanitize(action.payload.value, sanitizeProperty);

			if (subStep === "Address") {
				const validateInputAndForm = () => {
					validateAddress(
						"CarePlanCustomerAddress",
						sanitizeProperty as AddressFields,
						action.payload.value,
					);
					validateSideTab("CarePlanCustomerAddress");
				};
				debounceStorage.addOrExecute({
					name: action.payload.name,
					delay: FORM_VALIDATION_DELAY,
					callback: validateInputAndForm,
				});
			} else {
				const validateInputAndForm = () => {
					validateContact(
						"CarePlanCustomerContact",
						sanitizeProperty as ContactFields,
						action.payload.value,
					);
					validateSideTab("CarePlanCustomerContact");
				};
				debounceStorage.addOrExecute({
					name: action.payload.name,
					delay: FORM_VALIDATION_DELAY,
					callback: validateInputAndForm,
				});
			}

			switch (sanitizeProperty) {
				case "Title":
					Customer.title = action.payload.value;
					break;
				case "FirstName":
					Customer.forename = action.payload.value;
					break;
				case "LastName":
					Customer.surname = action.payload.value;
					break;
				case "Mobile":
					Customer.telephoneMobile = action.payload.value;
					break;
				case "Landline":
					Customer.telephoneLandline = action.payload.value;
					break;
				case "Email":
					Customer.emailAddress = action.payload.value;
					break;

				case "Property":
					Address.property = action.payload.value;
					break;

				case "Street":
					Address.street = action.payload.value;
					break;

				case "Locality":
					Address.locality = action.payload.value;
					break;

				case "City":
					Address.town = action.payload.value;
					break;

				case "Postcode":
					Address.postcode = action.payload.value;
					break;

				case "BusinessName":
					Address.organisation = action.payload.value;
					break;

				default:
					break;
			}

			return next(action);
		}

		case "CarePlanCustomerAddressAsProperty":
			if (action.payload.value) {
				// switch in position to copy data from the property address to the service agreement customer address
				serviceAgreement.serviceAgreementCustomerAddress.property =
					ui.PropertyAddressProperty;
				serviceAgreement.serviceAgreementCustomerAddress.street =
					ui.PropertyAddressStreet;
				serviceAgreement.serviceAgreementCustomerAddress.locality =
					ui.PropertyAddressLocality;
				serviceAgreement.serviceAgreementCustomerAddress.town =
					ui.PropertyAddressCity;
				serviceAgreement.serviceAgreementCustomerAddress.postcode =
					ui.PropertyAddressPostcode;
				serviceAgreement.serviceAgreementCustomerAddress.organisation =
					ui.PropertyAddressBusinessName;
			} else {
				// switch in position to not copy data
				serviceAgreement.serviceAgreementCustomerAddress.property =
					ui.CarePlanCustomerAddressProperty;
				serviceAgreement.serviceAgreementCustomerAddress.street =
					ui.CarePlanCustomerAddressStreet;
				serviceAgreement.serviceAgreementCustomerAddress.locality =
					ui.CarePlanCustomerAddressLocality;
				serviceAgreement.serviceAgreementCustomerAddress.town =
					ui.CarePlanCustomerAddressCity;
				serviceAgreement.serviceAgreementCustomerAddress.postcode =
					ui.CarePlanCustomerAddressPostcode;
				serviceAgreement.serviceAgreementCustomerAddress.organisation = "";
			}
			{
				const validateInputAndForm = () => {
					validateSideTab("CarePlanCustomerAddress");
				};
				debounceStorage.addOrExecute({
					name: action.payload.name,
					delay: FORM_VALIDATION_DELAY,
					callback: validateInputAndForm,
				});
			}
			return next(action);

		case "CarePlanCustomerAddressAsContact":
			if (action.payload.value) {
				// switch in position to copy data from the property contact to the service agreement customer contact
				serviceAgreement.serviceAgreementCustomer.emailAddress =
					ui.PropertyContactEmail;
				serviceAgreement.serviceAgreementCustomer.forename =
					ui.PropertyContactFirstName;
				serviceAgreement.serviceAgreementCustomer.surname =
					ui.PropertyContactLastName;
				serviceAgreement.serviceAgreementCustomer.telephoneLandline =
					ui.PropertyContactLandline;
				serviceAgreement.serviceAgreementCustomer.telephoneMobile =
					ui.PropertyContactMobile;
				serviceAgreement.serviceAgreementCustomer.title =
					ui.PropertyContactTitle ?? "";
			} else {
				// switch in position to not copy data
				serviceAgreement.serviceAgreementCustomer.emailAddress =
					ui.CarePlanCustomerContactEmail;
				serviceAgreement.serviceAgreementCustomer.forename =
					ui.CarePlanCustomerContactFirstName;
				serviceAgreement.serviceAgreementCustomer.surname =
					ui.CarePlanCustomerContactLastName;
				serviceAgreement.serviceAgreementCustomer.telephoneLandline =
					ui.CarePlanCustomerContactLandline;
				serviceAgreement.serviceAgreementCustomer.telephoneMobile =
					ui.CarePlanCustomerContactMobile;
				serviceAgreement.serviceAgreementCustomer.title =
					ui.CarePlanCustomerContactTitle ?? "";
			}
			{
				const validateInputAndForm = () => {
					validateSideTab("CarePlanCustomerContact");
				};
				debounceStorage.addOrExecute({
					name: action.payload.name,
					delay: FORM_VALIDATION_DELAY,
					callback: validateInputAndForm,
				});
			}
			return next(action);

		default:
			return next(action);
	}
};
